import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Card, CardActions, CardBody, CardHandle, CardHeader, CardTitle } from '@progress/kendo-react-layout';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { DragAndDrop, NormalizedDragEvent, SvgIcon, useDraggable, useDroppable } from '@progress/kendo-react-common';
import { paths } from '../../../routes';
import { fetchApi } from '../../../services/api';
import { Title } from '../../../utils/title';
import { EditableField } from '../../../components/EditableField';
import OwnerAutoComplete from '../../AssetOwners/OwnerAutoComplete';
import VehicleAutoComplete from '../../AssetVehicles/VehicleAutoComplete';
import TrailerAutoComplete from '../../AssetTrailers/TrailerAutoComplete';
import { JsonResponse } from 'TypeGen/json-response';
import AddWorkOrderTaskDialog from './AddWorkOrderTaskDialog';
import AddWorkOrderNoteDialog from './AddWorkOrderNoteDialog';
import CompleteWorkOrderTaskDialog from './CompleteWorkOrderTaskDialog';
import { plusIcon, chevronRightIcon, trashIcon, checkIcon, handleDragDotsIcon } from '@progress/kendo-svg-icons';
import { Badge, BadgeContainer } from '@progress/kendo-react-indicators';
import Documents, { DocumentEntityType } from 'views/Documents/Documents';
import { openWindow } from 'services/openWindow';
import WorkOrderTaskPartsGrid from './WorkOrderTaskPartsGrid';

export enum WorkOrderStatus {
  Open = 100,
  OnHold = 105,
  InProgress = 110,
  Vendor = 115,
  Completed = 120,
  Invoiced = 130,
  Paid = 140,
  Cancelled = 170,
}

export enum WorkOrderPriority {
  Low = 1,
  Normal = 5,
  High = 10,
  Critical = 20,
}

export enum WorkOrderKind {
  Repair = 0,
  CounterSale = 1,
}

// Azure AD "shopDL" group — technician dropdowns are limited to its members.
export const SHOP_GROUP_ID = 'feb0dbdd-a06b-4b66-b99e-e54dadecb853';

export enum WorkOrderTaskStatus {
  WaitingOnParts = 0,
  Diagnose = 1,
  Authorization = 2,
  RepairInProgress = 3,
  Completed = 4,
  Vendor = 5,
}

type StatusOption = { text: string; value: WorkOrderStatus };
type PriorityOption = { text: string; value: WorkOrderPriority };
type TaskStatusOption = { text: string; value: WorkOrderTaskStatus };

type ApplicationUser = {
  ID: string;
  Name: string;
};

type UsersDropdownResponse = {
  Users: ApplicationUser[];
  SelfID: string;
};

type WorkOrderDetailViewModel = {
  WorkOrderID: number;
  OwnerID: number;
  VehicleID?: number | null;
  TrailerID?: number | null;
  Status: WorkOrderStatus;
  Kind: WorkOrderKind;
  Priority: WorkOrderPriority;
  APUHours?: number | null;
  ChassisMiles?: number | null;
  ScheduledStartDate?: string | null;
  ActualStartDate?: string | null;
  CompletedDate?: string | null;
  InvoicedDate?: string | null;
  PaidDate?: string | null;
  ActualTotalCost: number;
  Description?: string | null;
  CreatedDateTime: string;
  OwnerName: string;
  VehicleNumber: string;
  TrailerNumber: string;
  VehicleVIN: string;
  TrailerVIN: string;
  CreatedByName: string;
  RowVersion?: string | null;
};

export type WorkOrderTaskPartViewModel = {
  WorkOrderTaskPartID: number;
  PartID?: number | null;
  PartLocationID?: number | null;
  PurchaseOrderNumber?: number | null;
  LocationID?: number | null;
  LocationName: string;
  IsPrimaryLocation: boolean;
  PartNumber: string;
  PartDescription: string;
  QuantityUsed: number;
  CostAtTime: number;
  Note?: string | null;
  IsCustomPart: boolean;
  CustomPartDescription?: string | null;
};

export type WorkOrderTaskDetailViewModel = {
  WorkOrderTaskID: number;
  WorkOrderID: number;
  Sequence: number;
  Status: WorkOrderTaskStatus;
  Description: string;
  AssignedTo: string | null;
  CompletedDate?: string | null;
  EstimatedHours?: number | null;
  ActualHours?: number | null;
  LaborRate: number;
  Note?: string | null;
  CreatedDateTime: string;
  AssignedToName: string;
  CreatedByName: string;
  Parts: WorkOrderTaskPartViewModel[];
};

type WorkOrderNoteViewModel = {
  WorkOrderNoteID: number;
  Note: string;
  CreatedDateTime: string;
  CreatedByName: string;
};

type WorkOrderDetailResponse = {
  DocumentCount: number;
  WorkOrder: WorkOrderDetailViewModel;
  Tasks: WorkOrderTaskDetailViewModel[];
  Notes: WorkOrderNoteViewModel[];
};

type RouteParams = {
  workOrderId: string;
};

type TaskDragWrapperProps = {
  task: WorkOrderTaskDetailViewModel;
  disabled?: boolean;
  onDragStart: (task: WorkOrderTaskDetailViewModel) => void;
  onDrop: (task: WorkOrderTaskDetailViewModel, direction: 'before' | 'after') => void;
  children: React.ReactNode;
};

const dropIndicatorStyle: React.CSSProperties = {
  height: 6,
  borderRadius: 3,
  backgroundColor: '#0d6efd',
  margin: '0.35rem 0'
};

const TaskDragWrapper: React.FC<TaskDragWrapperProps> = ({ task, disabled = false, onDragStart, onDrop, children }) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [dragged, setDragged] = useState(false);
  const [dropDirection, setDropDirection] = useState<'before' | 'after' | null>(null);
  const initialPosition = useRef({ x: 0, y: 0 });

  const resetTransform = () => {
    if (elementRef.current) {
      elementRef.current.style.transform = '';
    }
  };

  const handlePress = useCallback((event: NormalizedDragEvent) => {
    if (disabled) {
      return;
    }
    initialPosition.current = {
      x: event.clientX - event.offsetX,
      y: event.clientY - event.offsetY
    };
  }, [disabled]);

  const handleDragStart = useCallback((event: NormalizedDragEvent) => {
    if (disabled) {
      return;
    }
    const target = event.originalEvent?.target as HTMLElement | null;
    if (!target?.closest('[data-drag-handle="true"]')) {
      return;
    }
    setDragged(true);
    onDragStart(task);
  }, [disabled, onDragStart, task]);

  const handleDrag = useCallback((event: NormalizedDragEvent) => {
    if (!elementRef.current || !dragged) {
      return;
    }
    elementRef.current.style.transform = `translateY(${event.clientY - initialPosition.current.y + event.scrollY}px)`;
  }, [dragged]);

  const handleDragEnd = useCallback(() => {
    setDragged(false);
    setDropDirection(null);
    resetTransform();
  }, []);

  const handleRelease = useCallback(() => {
    resetTransform();
  }, []);

  const handleDragEnter = useCallback(() => {
    if (disabled) {
      return;
    }
    setDropDirection(null);
  }, [disabled]);

  const handleDragOver = useCallback((event: NormalizedDragEvent) => {
    if (disabled || !elementRef.current) {
      return;
    }
    const rect = elementRef.current.getBoundingClientRect();
    setDropDirection(rect.top + rect.height / 2 <= event.clientY ? 'after' : 'before');
  }, [disabled]);

  const handleDragLeave = useCallback(() => {
    setDropDirection(null);
  }, []);

  const handleDrop = useCallback(() => {
    if (disabled || !dropDirection) {
      setDropDirection(null);
      return;
    }
    onDrop(task, dropDirection);
    setDropDirection(null);
  }, [disabled, dropDirection, onDrop, task]);

  useDraggable(
    elementRef,
    {
      onPress: handlePress,
      onDragStart: handleDragStart,
      onDrag: handleDrag,
      onDragEnd: handleDragEnd,
      onRelease: handleRelease
    },
    { autoScroll: dragged }
  );

  useDroppable(elementRef, {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop
  });

  return (
    <>
      {dropDirection === 'before' && <div style={dropIndicatorStyle} />}
      <div
        ref={elementRef}
        style={{
          userSelect: 'none',
          pointerEvents: dragged ? 'none' : undefined,
          opacity: dragged ? 0.85 : 1
        }}
      >
        {children}
      </div>
      {dropDirection === 'after' && <div style={dropIndicatorStyle} />}
    </>
  );
};


const statusOptions: StatusOption[] = [
  { text: 'Open', value: WorkOrderStatus.Open },
  { text: 'On Hold', value: WorkOrderStatus.OnHold },
  { text: 'In Progress', value: WorkOrderStatus.InProgress },
  { text: 'Vendor', value: WorkOrderStatus.Vendor },
  { text: 'Completed', value: WorkOrderStatus.Completed },
  { text: 'Invoiced', value: WorkOrderStatus.Invoiced },
  { text: 'Paid', value: WorkOrderStatus.Paid },
  { text: 'Cancelled', value: WorkOrderStatus.Cancelled }
];

const priorityOptions: PriorityOption[] = [
  { text: 'Low', value: WorkOrderPriority.Low },
  { text: 'Normal', value: WorkOrderPriority.Normal },
  { text: 'High', value: WorkOrderPriority.High },
  { text: 'Critical', value: WorkOrderPriority.Critical }
];

const reorderLockedStatuses: WorkOrderStatus[] = [
  WorkOrderStatus.Completed,
  WorkOrderStatus.Invoiced,
  WorkOrderStatus.Paid
];

const taskStatusOptions: TaskStatusOption[] = [
  { text: 'Waiting On Parts', value: WorkOrderTaskStatus.WaitingOnParts },
  { text: 'Diagnose', value: WorkOrderTaskStatus.Diagnose },
  { text: 'Authorization', value: WorkOrderTaskStatus.Authorization },
  { text: 'Repair In Progress', value: WorkOrderTaskStatus.RepairInProgress },
  { text: 'Vendor', value: WorkOrderTaskStatus.Vendor },
  { text: 'Completed', value: WorkOrderTaskStatus.Completed }
];

const statusLabels: Record<number, string> = {
  [WorkOrderStatus.Open]: 'Open',
  [WorkOrderStatus.OnHold]: 'On Hold',
  [WorkOrderStatus.InProgress]: 'In Progress',
  [WorkOrderStatus.Vendor]: 'Vendor',
  [WorkOrderStatus.Completed]: 'Completed',
  [WorkOrderStatus.Invoiced]: 'Invoiced',
  [WorkOrderStatus.Paid]: 'Paid',
  [WorkOrderStatus.Cancelled]: 'Cancelled',
  [WorkOrderTaskStatus.WaitingOnParts]: 'Waiting On Parts',
  [WorkOrderTaskStatus.Diagnose]: 'Diagnose',
  [WorkOrderTaskStatus.Authorization]: 'Authorization',
  [WorkOrderTaskStatus.RepairInProgress]: 'Repair In Progress',
  [WorkOrderTaskStatus.Completed]: 'Completed',
  [WorkOrderTaskStatus.Vendor]: 'Vendor',
};

const priorityLabels: Record<number, string> = {
  [WorkOrderPriority.Low]: 'Low',
  [WorkOrderPriority.Normal]: 'Normal',
  [WorkOrderPriority.High]: 'High',
  [WorkOrderPriority.Critical]: 'Critical'
};

const getStatusBadgeColor = (status: WorkOrderStatus | WorkOrderTaskStatus) => {
  switch (status) {
    case WorkOrderStatus.Open:
    case WorkOrderTaskStatus.Diagnose:
      return 'info';
    case WorkOrderStatus.OnHold:
      return 'secondary';
    case WorkOrderStatus.InProgress:
    case WorkOrderTaskStatus.Authorization:
    case WorkOrderTaskStatus.RepairInProgress:
      return 'warning';
    case WorkOrderStatus.Completed:
    case WorkOrderStatus.Paid:
    case WorkOrderTaskStatus.Completed:
      return 'success';
    case WorkOrderStatus.Invoiced:
      return 'primary';
    case WorkOrderStatus.Cancelled:
      return 'danger';
    case WorkOrderTaskStatus.WaitingOnParts:
    default:
      return 'secondary';
  }
};

const getPriorityBadgeColor = (priority: WorkOrderPriority) => {
  switch (priority) {
    case WorkOrderPriority.Low:
      return 'secondary';
    case WorkOrderPriority.Normal:
      return 'info';
    case WorkOrderPriority.High:
      return 'warning';
    case WorkOrderPriority.Critical:
    default:
      return 'danger';
  }
};

export const getStatusBadgeStyle = (status: WorkOrderStatus | WorkOrderTaskStatus): React.CSSProperties | undefined =>
  status === WorkOrderStatus.Vendor || status === WorkOrderTaskStatus.Vendor ? { backgroundColor: '#9b7fd4', color: '#fff' } : undefined;

export const formatStatusLabel = (status: WorkOrderStatus | WorkOrderTaskStatus) => statusLabels[status] ?? String(status);
export const formatPriorityLabel = (priority: WorkOrderPriority) => priorityLabels[priority] ?? String(priority);

export const formatCurrency = (value?: number | null) => {
  const amount = value ?? 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

const formatDateTime = (dateString?: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

const ShopWorkOrderDetail: React.FC = () => {
  const { workOrderId } = useParams<RouteParams>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WorkOrderDetailResponse | null>(null);
  const [showAddTaskDialog, setShowAddTaskDialog] = useState(false);
  const [showAddNoteDialog, setShowAddNoteDialog] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [users, setUsers] = useState<ApplicationUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [taskBeingCompleted, setTaskBeingCompleted] = useState<WorkOrderTaskDetailViewModel | null>(null);
  const [reopeningTaskId, setReopeningTaskId] = useState<number | null>(null);
  const [draggingTaskId, setDraggingTaskId] = useState<number | null>(null);
  const [reorderingTaskOrder, setReorderingTaskOrder] = useState(false);
  const detailsPanelRef = useRef<CardHandle | null>(null);
  const [detailsPanelHeight, setDetailsPanelHeight] = useState<number | null>(null);
  const workOrderIdNumber = data?.WorkOrder.WorkOrderID ?? (workOrderId ? parseInt(workOrderId, 10) : NaN);
  const canReorderTasks = Boolean(data && data.Tasks.length > 1 && !reorderLockedStatuses.includes(data.WorkOrder.Status));

  const loadData = useCallback(async (showSpinner = true) => {
    if (!workOrderId) return;

    if (showSpinner) {
      setLoading(true);
    }

    try {
      const response = await fetchApi(`/api/ShopWorkOrder/WorkOrderDetail?WorkOrderID=${workOrderId}`) as WorkOrderDetailResponse;
      setData(response);
    } catch (error) {
      console.error('Error loading work order details:', error);
      if (showSpinner) {
        setData(null);
      }
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
    }
  }, [workOrderId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useLayoutEffect(() => {
    if (!detailsPanelRef.current) {
      return;
    }

    const updateHeight = () => {
      if (detailsPanelRef.current) {
        setDetailsPanelHeight(detailsPanelRef.current!.element.getBoundingClientRect().height);
      }
    };

    updateHeight();

    // Optional: keep in sync on window resize
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [data]);

  useEffect(() => {
    let isMounted = true;
    setLoadingUsers(true);
    fetchApi(`/api/User/UsersDropdown?groupId=${SHOP_GROUP_ID}`)
      .then((response: UsersDropdownResponse) => {
        if (!isMounted) return;
        setUsers(response.Users ?? []);
      })
      .catch((error) => {
        console.error('Failed to load users dropdown.', error);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoadingUsers(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const updateWorkOrder = useCallback(async (changes: Partial<WorkOrderDetailViewModel>) => {
    if (!data) return;

    const updatedWorkOrder: WorkOrderDetailViewModel = { ...data.WorkOrder, ...changes };

    const payload = {
      WorkOrderID: updatedWorkOrder.WorkOrderID,
      OwnerID: updatedWorkOrder.OwnerID,
      VehicleID: updatedWorkOrder.VehicleID ?? null,
      TrailerID: updatedWorkOrder.TrailerID ?? null,
      Status: updatedWorkOrder.Status,
      Priority: updatedWorkOrder.Priority,
      APUHours: updatedWorkOrder.APUHours ?? null,
      ChassisMiles: updatedWorkOrder.ChassisMiles ?? null,
      ScheduledStartDate: updatedWorkOrder.ScheduledStartDate ?? null,
      ActualStartDate: updatedWorkOrder.ActualStartDate ?? null,
      Description: updatedWorkOrder.Description ?? null,
      RowVersion: updatedWorkOrder.RowVersion ?? null
    };

    try {
      const response = await fetchApi('/api/ShopWorkOrder/UpdateWorkOrder', payload, 'POST') as JsonResponse;
      if (!response?.Success) {
        alert(response?.ErrorMessage || 'Unable to update work order.');
        throw new Error(response?.ErrorMessage || 'Unable to update work order.');
      }
      await loadData(false);
    } catch (error) {
      console.error('Error updating work order:', error);
      throw error;
    }
  }, [data, loadData]);

  const updateWorkOrderTask = useCallback(async (taskId: number, changes: Partial<WorkOrderTaskDetailViewModel>) => {
    if (!data) {
      throw new Error('Work order data not loaded.');
    }

    const existingTask = data.Tasks.find((task) => task.WorkOrderTaskID === taskId);
    if (!existingTask) {
      throw new Error('Work order task not found.');
    }

    const hasEstimatedHoursChange = Object.prototype.hasOwnProperty.call(changes, 'EstimatedHours');
    const hasActualHoursChange = Object.prototype.hasOwnProperty.call(changes, 'ActualHours');
    const hasNoteChange = Object.prototype.hasOwnProperty.call(changes, 'Note');
    const hasAssignedToChange = Object.prototype.hasOwnProperty.call(changes, 'AssignedTo');

    const assignedToValue = hasAssignedToChange
      ? (changes.AssignedTo ? changes.AssignedTo : null)
      : (existingTask.AssignedTo ? existingTask.AssignedTo : null);

    const payload = {
      WorkOrderTaskID: existingTask.WorkOrderTaskID,
      Status: changes.Status ?? existingTask.Status,
      Description: changes.Description ?? existingTask.Description,
      AssignedTo: assignedToValue,
      EstimatedHours: hasEstimatedHoursChange ? (changes.EstimatedHours ?? null) : (existingTask.EstimatedHours ?? null),
      ActualHours: hasActualHoursChange ? (changes.ActualHours ?? null) : (existingTask.ActualHours ?? null),
      LaborRate: changes.LaborRate ?? existingTask.LaborRate,
      Note: hasNoteChange ? (changes.Note ?? null) : (existingTask.Note ?? null)
    };

    try {
      const response = await fetchApi('/api/ShopWorkOrder/UpdateWorkOrderTask', payload, 'POST') as JsonResponse;
      if (!response?.Success) {
        alert(response?.ErrorMessage || 'Unable to update work order task.');
        throw new Error(response?.ErrorMessage || 'Unable to update work order task.');
      }
      await loadData(false);
    } catch (error) {
      console.error('Error updating work order task:', error);
      throw error;
    }
  }, [data, loadData]);

  const deleteWorkOrderTask = useCallback(async (task: WorkOrderTaskDetailViewModel) => {
    if (task.Status === WorkOrderTaskStatus.Completed) {
      alert('Completed tasks cannot be deleted. Reopen the task before deleting.');
      return;
    }

    const confirmDelete = window.confirm('Delete this task? Any associated parts will also be removed.');
    if (!confirmDelete) {
      return;
    }

    try {
      const response = await fetchApi('/api/ShopWorkOrder/DeleteWorkOrderTask', { WorkOrderTaskID: task.WorkOrderTaskID }, 'POST') as JsonResponse;
      if (!response?.Success) {
        alert(response?.ErrorMessage || 'Unable to delete work order task.');
        return;
      }

      await loadData();
    } catch (error) {
      console.error('Error deleting work order task:', error);
      alert('Unable to delete work order task.');
    }
  }, [loadData]);

  const reopenWorkOrderTask = useCallback(async (task: WorkOrderTaskDetailViewModel) => {
    const confirmReopen = window.confirm('Reopening this task will return any parts used to inventory and move the task back to Repair In Progress. Continue?');
    if (!confirmReopen) {
      return;
    }

    const noteInput = window.prompt('Optional: add a note about why this task is being reopened.', '');
    const trimmedNote = noteInput?.trim();

    setReopeningTaskId(task.WorkOrderTaskID);

    const payload = {
      WorkOrderTaskID: task.WorkOrderTaskID,
      Note: trimmedNote && trimmedNote.length ? trimmedNote : null
    };

    fetchApi('/api/ShopWorkOrder/ReopenWorkOrderTask', payload, 'POST')
      .then(async (response: JsonResponse) => {
        if (response.Success == false) {
          alert(response?.ErrorMessage || 'Unable to reopen task.');
        } else {
          await loadData();
        }
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Unable to reopen task.');
      }).finally(() => {
        setReopeningTaskId(null);
      });
  }, [loadData]);

  const handleNoteCreated = useCallback(() => {
    setShowAddNoteDialog(false);
    loadData(false);
  }, [loadData]);

  const handleTaskDragStart = useCallback((task: WorkOrderTaskDetailViewModel) => {
    if (!canReorderTasks || reorderingTaskOrder) {
      return;
    }
    setDraggingTaskId(task.WorkOrderTaskID);
  }, [canReorderTasks, reorderingTaskOrder]);

  const handleTaskDrop = useCallback(async (targetTask: WorkOrderTaskDetailViewModel, direction: 'before' | 'after') => {
    if (!canReorderTasks || reorderingTaskOrder) {
      setDraggingTaskId(null);
      return;
    }

    if (!data || draggingTaskId == null || draggingTaskId === targetTask.WorkOrderTaskID) {
      setDraggingTaskId(null);
      return;
    }

    const currentTasks = data.Tasks;
    const sourceIndex = currentTasks.findIndex((task) => task.WorkOrderTaskID === draggingTaskId);
    const targetIndex = currentTasks.findIndex((task) => task.WorkOrderTaskID === targetTask.WorkOrderTaskID);

    if (sourceIndex === -1 || targetIndex === -1) {
      setDraggingTaskId(null);
      return;
    }

    const updatedTasks = [...currentTasks];
    const [movedTask] = updatedTasks.splice(sourceIndex, 1);
    let insertIndex = updatedTasks.findIndex((task) => task.WorkOrderTaskID === targetTask.WorkOrderTaskID);
    if (insertIndex < 0) {
      setDraggingTaskId(null);
      return;
    }
    if (direction === 'after') {
      insertIndex += 1;
    }
    updatedTasks.splice(insertIndex, 0, movedTask);

    const tasksWithSequence = updatedTasks.map((task, idx) => ({ ...task, Sequence: idx + 1 }));
    setData((prev) => (prev ? { ...prev, Tasks: tasksWithSequence } : prev));
    setReorderingTaskOrder(true);

    try {
      const payload = {
        WorkOrderID: data.WorkOrder.WorkOrderID,
        OrderedTaskIDs: tasksWithSequence.map((task) => task.WorkOrderTaskID)
      };

      const response = await fetchApi('/api/ShopWorkOrder/ReorderWorkOrderTasks', payload, 'POST') as JsonResponse;
      if (!response?.Success) {
        throw new Error(response?.ErrorMessage || 'Unable to reorder tasks.');
      }
    } catch (error) {
      console.error('Error reordering work order tasks:', error);
      alert(error instanceof Error ? error.message : 'Unable to reorder tasks.');
      await loadData(false);
    } finally {
      setReorderingTaskOrder(false);
      setDraggingTaskId(null);
    }
  }, [canReorderTasks, data, draggingTaskId, loadData, reorderingTaskOrder]);

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center p-4">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">
          Work order not found or error loading data.
        </div>
      </div>
    );
  }

  const { WorkOrder, Tasks, Notes } = data;

  const renderTaskCard = (task: WorkOrderTaskDetailViewModel) => (
    <Card className="mb-4">
      <CardHeader>
          <div className="d-flex align-items-center">
            {canReorderTasks && (
              <button
                type="button"
                className="btn btn-link p-0 mr-2 text-muted"
                data-drag-handle="true"
                title="Drag to reorder"
                disabled={reorderingTaskOrder}
                style={{ cursor: reorderingTaskOrder ? 'not-allowed' : 'grab' }}
              >
                <SvgIcon icon={handleDragDotsIcon} />
              </button>
            )}
            <CardTitle className="mb-0">
              #{task.Sequence} - {task.AssignedToName || 'Unassigned'} - <span className="text-muted">{statusLabels[task.Status]}</span>
            </CardTitle>
          </div>
      </CardHeader>
      <CardBody className='pb-0'>
        <div className="mb-3">
          <strong>Concern:</strong>
          <EditableField
            required
            data={task.Description}
            renderDisplay={(description) => description?.length ? description : 'No description provided.'}
            renderEditable={(description, saving, onChange) => (
              <textarea
                autoFocus
                className="form-control"
                rows={3}
                maxLength={2048}
                disabled={saving}
                value={description ?? ''}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
            save={async (description) => {
              const trimmed = description.trim();
              if (!trimmed) {
                alert('Description is required.');
                throw new Error('Description is required.');
              }
              await updateWorkOrderTask(task.WorkOrderTaskID, { Description: trimmed });
            }}
          />
        </div>

        <div className="mb-3">
          <strong>Cause/Correction:</strong>
          <EditableField
            data={task.Note ?? ''}
            renderDisplay={(note) => note?.length ? note : 'N/A'}
            renderEditable={(note, saving, onChange) => (
              <textarea
                autoFocus
                className="form-control"
                rows={3}
                maxLength={2048}
                disabled={saving}
                value={note ?? ''}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
            save={async (note) => {
              const trimmedNote = note.trim();
              await updateWorkOrderTask(task.WorkOrderTaskID, { Note: trimmedNote.length ? trimmedNote : null });
            }}
          />
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="row align-items-center mb-3">
              <div className="col-4">
                <strong>Status:</strong>
              </div>
              <div className="col-8">
                <EditableField
                  data={task.Status}
                  readOnly={task.Status === WorkOrderTaskStatus.Completed}
                  renderDisplay={(status) => (
                    <span
                      className={`badge badge-${getStatusBadgeColor(status as WorkOrderTaskStatus)}`}
                      style={getStatusBadgeStyle(status as WorkOrderTaskStatus)}
                    >
                      {formatStatusLabel(status as WorkOrderTaskStatus)}
                    </span>
                  )}
                  renderEditable={(status, saving, onChange) => {
                    const availableStatusOptions = taskStatusOptions.filter((option) => option.value !== WorkOrderTaskStatus.Completed);
                    return (
                      <DropDownList
                        style={{ width: '100%', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                        data={availableStatusOptions}
                        textField="text"
                        dataItemKey="value"
                        value={availableStatusOptions.find((option) => option.value === status) ?? null}
                        disabled={saving}
                        onChange={(e) => onChange(e.target.value.value as WorkOrderTaskStatus)}
                      />
                    );
                  }}
                  save={async (status) => {
                    await updateWorkOrderTask(task.WorkOrderTaskID, { Status: status });
                  }}
                />
              </div>
            </div>
            <div className="row align-items-center mb-3">
              <div className="col-4">
                <strong>Assigned To:</strong>
              </div>
              <div className="col-8">
                <EditableField
                  data={task.AssignedTo}
                  renderDisplay={(assignedTo) => {
                    const user = users.find((u) => u.ID === assignedTo);
                    return user?.Name || task.AssignedToName || 'Unassigned';
                  }}
                  renderEditable={(assignedTo, saving, onChange) => (
                    <DropDownList
                      style={{ width: '100%', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                      loading={loadingUsers}
                      textField="Name"
                      dataItemKey="ID"
                      data={users}
                      value={users.find((u) => u.ID === assignedTo) ?? null}
                      disabled={saving || loadingUsers}
                      onChange={(e) => onChange(e.target.value.ID)}
                    />
                  )}
                  save={async (assignedTo) => {
                    await updateWorkOrderTask(task.WorkOrderTaskID, { AssignedTo: assignedTo ?? null });
                  }}
                />
              </div>
            </div>
            <div className="row align-items-center mb-3">
              <div className="col-4">
                <strong>Estimated Hours:</strong>
              </div>
              <div className="col-8">
                <EditableField
                  dataType="number"
                  inputProps={{ min: 0, step: 0.5, format: 'n1' }}
                  data={task.EstimatedHours ?? null}
                  renderDisplay={(value) => value?.toString() ?? 'N/A'}
                  save={async (value) => {
                    await updateWorkOrderTask(task.WorkOrderTaskID, { EstimatedHours: value ?? null });
                  }}
                />
              </div>
            </div>
            <div className="row align-items-center mb-3">
              <div className="col-4">
                <strong>Actual Hours:</strong>
              </div>
              <div className="col-8">
                <EditableField
                  dataType="number"
                  inputProps={{ min: 0, step: 0.5, format: 'n1' }}
                  data={task.ActualHours ?? null}
                  renderDisplay={(value) => value?.toString() ?? 'N/A'}
                  save={async (value) => {
                    await updateWorkOrderTask(task.WorkOrderTaskID, { ActualHours: value ?? null });
                  }}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="row align-items-center mb-3">
              <div className="col-4">
                <strong>Labor Rate:</strong>
              </div>
              <div className="col-8">
                <EditableField
                  dataType="number"
                  inputProps={{ min: 0, step: 1 }}
                  data={task.LaborRate}
                  renderDisplay={(value) => formatCurrency(value)}
                  save={async (value) => {
                    await updateWorkOrderTask(task.WorkOrderTaskID, { LaborRate: value ?? 0 });
                  }}
                />
              </div>
            </div>
            <div className="row align-items-center mb-3">
              <div className="col-4">
                <strong>Assigned By:</strong>
              </div>
              <div className="col-8">
                <p className="mb-0">{task.CreatedByName || 'Unknown'}</p>
                <small className="text-muted">{formatDateTime(task.CreatedDateTime)}</small>
              </div>
            </div>
          </div>
        </div>

        <WorkOrderTaskPartsGrid
          key={`${task.WorkOrderTaskID}-${task.Parts.map((part) => [
            part.WorkOrderTaskPartID,
            part.QuantityUsed,
            part.CostAtTime,
            part.PartLocationID ?? '',
            part.PurchaseOrderNumber ?? ''
          ].join(':')).join('|')}`}
          task={task}
          onRefresh={() => loadData(false)}
          disabled={task.Status === WorkOrderTaskStatus.Completed}
        />
      </CardBody>
      <CardActions>
        {task.Status === WorkOrderTaskStatus.Completed ? (
          <Button
            themeColor="warning"
            className="ml-2"
            disabled={reopeningTaskId === task.WorkOrderTaskID}
            onClick={() => reopenWorkOrderTask(task)}
          >
            Reopen Task
          </Button>
        ) : (
          <>
            <Button
              svgIcon={checkIcon}
              themeColor="success"
              onClick={() => setTaskBeingCompleted(task)}
            >
              Complete Task
            </Button>
            <Button
              svgIcon={trashIcon}
              themeColor="error"
              onClick={() => deleteWorkOrderTask(task)}
            >
              Delete Task
            </Button>
          </>
        )}
      </CardActions>
    </Card>
  );


  return <>
    <Title string={`Work Order ${WorkOrder.WorkOrderID}`} />
    {showAddTaskDialog && (
      <AddWorkOrderTaskDialog
        workOrderId={WorkOrder.WorkOrderID}
        onClose={() => {
          setShowAddTaskDialog(false);
        }}
        onTaskCreated={() => {
          setShowAddTaskDialog(false);
          loadData(false);
        }}
      />
    )}

    {taskBeingCompleted && (
      <CompleteWorkOrderTaskDialog
        task={taskBeingCompleted}
        onClose={() => setTaskBeingCompleted(null)}
        onCompleted={() => {
          setTaskBeingCompleted(null);
          loadData();
        }}
      />
    )}

    {showDocumentDialog &&
      <Documents
        IsWindow
        EntityType={DocumentEntityType.ShopWorkOrder}
        EntityId={WorkOrder.WorkOrderID}
        EntityNumber={`Work Order # ${WorkOrder.WorkOrderID}`}
        CloseDocumentModal={(documentCount) => {
          setShowDocumentDialog(false);
          setData((prevData) => {
            return { ...prevData, DocumentCount: documentCount };
          });
        }}
      />
    }

    <nav className="navbar navbar-expand-lg navbar-dark bg-dark mt-3">
      <span className="navbar-brand">
        <Link to={paths.ShopWorkOrders}>Work Orders</Link>
        <span className="text-muted">
          <SvgIcon icon={chevronRightIcon} />
          {WorkOrder.Kind === WorkOrderKind.CounterSale ? 'Counter Sale' : 'Work Order'} {WorkOrder.WorkOrderID}
        </span>
      </span>
      <button className="navbar-toggler" type="button" data-toggle="collapse" data-target="#workOrderNav" aria-controls="workOrderNav" aria-expanded="false" aria-label="Toggle navigation">
        <span className="navbar-toggler-icon"></span>
      </button>
      <div className="collapse navbar-collapse" id="workOrderNav">
        <ul className="nav navbar-nav ml-auto">
          <li className="nav-item">
            <BadgeContainer className="mr-2">
              <Button themeColor="info" onClick={() => setShowDocumentDialog(true)}>Documents</Button>
              {data.DocumentCount > 0 && <Badge>{data.DocumentCount}</Badge>}
            </BadgeContainer>
            <Button themeColor='success' title="Actual Cost" onClick={() => openWindow(`/ShopWorkOrderInvoice/View/${WorkOrder.WorkOrderID}`)} className="mr-2">
              Invoice {formatCurrency(WorkOrder.ActualTotalCost)}
            </Button>
            <Button themeColor="success" svgIcon={plusIcon} onClick={() => setShowAddTaskDialog(true)}>
              Add Task
            </Button>
          </li>
        </ul>
      </div>
    </nav>

    <div className="row mb-4 mt-3">
      <div className="col-md-8">
        <Card ref={detailsPanelRef}>
          <CardHeader>
            <CardTitle>
              Work Order {WorkOrder.WorkOrderID} - <span className="text-muted">{formatStatusLabel(WorkOrder.Status)}</span>
            </CardTitle>
          </CardHeader>
          <CardBody>
            <table className="table table-borderless align-middle">
              <tbody>
                <tr>
                  <td className="w-25"><strong>Owner:</strong></td>
                  <td className="w-25">
                    <EditableField
                      required
                      data={WorkOrder.OwnerID}
                      open={WorkOrder.OwnerID ? () => openWindow(`/Assets/Owner/${WorkOrder.OwnerID}`) : null}
                      renderDisplay={() => WorkOrder.OwnerName}
                      renderEditable={(ownerId, saving, onChange) => (
                        <OwnerAutoComplete
                          selectedOwnerID={ownerId}
                          onSelectedOwner={(owner) => {
                            onChange(owner?.OwnerID ?? 0);
                          }}
                          disabled={saving}
                          style={{ width: '100%', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                        />
                      )}
                      save={async (ownerId) => {
                        if (!ownerId) {
                          alert('Owner is required.');
                          throw new Error('Owner is required.');
                        }
                        await updateWorkOrder({ OwnerID: ownerId });
                      }}
                    />
                  </td>
                  <td className="w-25"><strong>Vehicle:</strong></td>
                  <td className="w-25">
                    <EditableField
                      data={WorkOrder.VehicleID ?? null}
                      open={WorkOrder.VehicleID ? () => openWindow(`/Assets/Vehicle/${WorkOrder.VehicleID}`) : null}
                      renderDisplay={(vehicleId) => vehicleId ? (WorkOrder.VehicleNumber || 'N/A') : 'None'}
                      renderEditable={(vehicleId, saving, onChange) => (
                        <VehicleAutoComplete
                          selectedVehicleID={vehicleId ?? undefined}
                          onSelectedVehicle={(vehicle) => {
                            onChange(vehicle?.VehicleID ?? null);
                          }}
                          disabled={saving}
                          style={{ width: '100%', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                        />
                      )}
                      save={async (vehicleId) => {
                        await updateWorkOrder({ VehicleID: vehicleId ?? null });
                      }}
                    />
                    {WorkOrder.VehicleVIN && <small className="text-muted d-block" style={{ paddingLeft: '.75rem' }}>VIN: {WorkOrder.VehicleVIN}</small>}
                  </td>
                </tr>
                <tr>
                  <td><strong>Trailer:</strong></td>
                  <td>
                    <EditableField
                      data={WorkOrder.TrailerID ?? null}
                      open={WorkOrder.TrailerID ? () => openWindow(`/Assets/Trailer/${WorkOrder.TrailerID}`) : null}
                      renderDisplay={(trailerId) => trailerId ? (WorkOrder.TrailerNumber || 'N/A') : 'None'}
                      renderEditable={(trailerId, saving, onChange) => (
                        <TrailerAutoComplete
                          selectedTrailerID={trailerId ?? undefined}
                          onSelectedTrailer={(trailer) => {
                            onChange(trailer?.TrailerID ?? null);
                          }}
                          disabled={saving}
                          style={{ width: '100%', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                        />
                      )}
                      save={async (trailerId) => {
                        await updateWorkOrder({ TrailerID: trailerId ?? null });
                      }}
                    />
                    {WorkOrder.TrailerVIN && <small className="text-muted d-block" style={{ paddingLeft: '.75rem' }}>VIN: {WorkOrder.TrailerVIN}</small>}
                  </td>
                  <td><strong>APU Hours:</strong></td>
                  <td>
                    <EditableField
                      dataType="number"
                      inputProps={{ format: 'n1', min: 0, step: 0.5 }}
                      data={WorkOrder.APUHours ?? null}
                      save={async (value) => {
                        await updateWorkOrder({ APUHours: value ?? null });
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td><strong>Chassis Miles:</strong></td>
                  <td>
                    <EditableField
                      dataType="number"
                      inputProps={{ format: 'n1', min: 0 }}
                      data={WorkOrder.ChassisMiles ?? null}
                      save={async (value) => {
                        await updateWorkOrder({ ChassisMiles: value ?? null });
                      }}
                    />
                  </td>
                  <td><strong>Scheduled Start:</strong></td>
                  <td>
                    <EditableField
                      dataType="dateonly"
                      data={WorkOrder.ScheduledStartDate ?? null}
                      renderDisplay={(value) => formatDate(value)}
                      save={async (value) => {
                        await updateWorkOrder({ ScheduledStartDate: value ?? null });
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td><strong>Actual Start:</strong></td>
                  <td>
                    <EditableField
                      dataType="dateonly"
                      data={WorkOrder.ActualStartDate ?? null}
                      renderDisplay={(value) => formatDate(value)}
                      save={async (value) => {
                        await updateWorkOrder({ ActualStartDate: value ?? null });
                      }}
                    />
                  </td>
                  <td><strong>Completed:</strong></td>
                  <td><span className="disabled text-left btn btn-block">{formatDate(WorkOrder.CompletedDate)}</span></td>
                </tr>
                <tr>
                  <td><strong>Status:</strong></td>
                  <td>
                    <EditableField
                      data={WorkOrder.Status}
                      renderDisplay={(status) => <span className={`badge badge-${getStatusBadgeColor(status)}`} style={getStatusBadgeStyle(status)}>{formatStatusLabel(status)}</span>}
                      renderEditable={(data, saving, onChange) => <select disabled={saving} className="custom-select" value={data} onChange={(e) => onChange(parseInt(e.target.value))}>
                        {statusOptions.map(({ text, value }) => <option key={value} value={value}>{text}</option>)}
                      </select>}
                      save={async (status) => {
                        await updateWorkOrder({ Status: status });
                      }}
                    />
                  </td>
                  <td><strong>Priority:</strong></td>
                  <td>
                    <EditableField
                      data={WorkOrder.Priority}
                      renderDisplay={(priority) => <span className={`badge badge-${getPriorityBadgeColor(priority)}`}>{formatPriorityLabel(priority)}</span>}
                      renderEditable={(data, saving, onChange) => <select disabled={saving} className="custom-select" value={data} onChange={(e) => onChange(parseInt(e.target.value))}>
                        {priorityOptions.map(({ text, value }) => <option key={value} value={value}>{text}</option>)}
                      </select>}
                      save={async (priority) => {
                        await updateWorkOrder({ Priority: priority });
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td><strong>Written:</strong></td>
                  <td><span className="disabled text-left btn btn-block">{formatDateTime(WorkOrder.CreatedDateTime)}</span></td>
                  <td><strong>Service Writer:</strong></td>
                  <td><span className="disabled text-left btn btn-block">{WorkOrder.CreatedByName}</span></td>
                </tr>
                <tr>
                  <td><strong>Invoiced:</strong></td>
                  <td><span className="disabled text-left btn btn-block">{formatDate(WorkOrder.InvoicedDate)}</span></td>
                  <td><strong>Paid:</strong></td>
                  <td><span className="disabled text-left btn btn-block">{formatDate(WorkOrder.PaidDate)}</span></td>
                </tr>
              </tbody>
            </table>

            <div className="mt-3">
              <strong>Customer Concern:</strong>
              <EditableField
                data={WorkOrder.Description ?? ''}
                renderDisplay={(description) => description?.length ? description : 'No description provided.'}
                renderEditable={(description, saving, onChange) => (
                  <textarea
                    className="form-control"
                    rows={3}
                    maxLength={500}
                    disabled={saving}
                    value={description ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
                save={async (description) => {
                  await updateWorkOrder({ Description: description?.trim() ? description : null });
                }}
              />
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="col-md-4" >
        <Card
          className="d-flex flex-column"
          style={{
            maxHeight: detailsPanelHeight ? `${detailsPanelHeight}px` : "none",
            overflowY: "auto",
          }}
        >
          <CardHeader className="d-flex justify-content-between align-items-center">
            <CardTitle className="mb-0">Notes</CardTitle>
            <Button
              size="small"
              fillMode='link'
              className='py-0'
              svgIcon={plusIcon}
              themeColor="primary"
              onClick={() => setShowAddNoteDialog(true)}
              disabled={!Number.isFinite(workOrderIdNumber)}
            >
              Add Note
            </Button>
          </CardHeader>
          <CardBody className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
            {Notes.length === 0 ? (
              <p className="text-muted mb-0">No notes added to this work order.</p>
            ) : (
              Notes.map((note) => (
                <Card key={note.WorkOrderNoteID} className="mb-3">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <small className="text-muted">
                        {formatDateTime(note.CreatedDateTime)} by {note.CreatedByName}
                      </small>
                    </div>
                    <p className="mb-0">{note.Note}</p>
                  </CardBody>
                </Card>
              ))
            )}
          </CardBody>
        </Card>
      </div>
    </div>

    <div className="row">
      <div className="col-12">
        {Tasks.length === 0 ? (
          <Card className="mb-4">
            <CardBody>
              <p className="text-muted mb-0">No tasks for this work order.</p>
            </CardBody>
          </Card>
        ) : (
          <>
            {reorderingTaskOrder && canReorderTasks && (
              <div className="alert alert-info py-2 px-3 mb-3">
                Saving task order...
              </div>
            )}
            {canReorderTasks ? (
              <DragAndDrop>
                {Tasks.map((task) => (
                  <TaskDragWrapper
                    key={task.WorkOrderTaskID}
                    task={task}
                    disabled={reorderingTaskOrder}
                    onDragStart={handleTaskDragStart}
                    onDrop={handleTaskDrop}
                  >
                    {renderTaskCard(task)}
                  </TaskDragWrapper>
                ))}
              </DragAndDrop>
            ) : (
              Tasks.map((task) => (
                <React.Fragment key={task.WorkOrderTaskID}>
                  {renderTaskCard(task)}
                </React.Fragment>
              ))
            )}
          </>
        )}
      </div>
    </div>


    {showAddNoteDialog && Number.isFinite(workOrderIdNumber) && (
      <AddWorkOrderNoteDialog
        workOrderId={Number(workOrderIdNumber)}
        onClose={() => setShowAddNoteDialog(false)}
        onNoteCreated={handleNoteCreated}
      />
    )}
  </>;
};

export default ShopWorkOrderDetail;
